/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: StudentOverview.tsx
 * Author: Austin Robinson
 * Date Created: 4/6/2022
 * Copyright (c) DrivingDoc 2022
 */
import { useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/GridLegacy';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import moment from 'moment';
import { Link } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

// import usePermitUpdateDialog from '@/components/users/Students/components/usePermitUpdateDialog';
// import { updateStudentDrive } from '@/layouts/admin/scheduling/mutations';
import { useCancelPeriodEnrollment } from 'api/hooks/private/periods/CancelPeriodEnrollment.ts';
import { useDeletePeriodEnrollment } from 'api/hooks/private/periods/DeletePeriodEnrollment.ts';
import { CoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod.ts';
import { TakeAttendanceParams, useTakeAttendance } from 'api/hooks/private/periods/TakeAttendance.ts';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog/FeedbackDialog.tsx';

import useAuthContext from 'hooks/useAuthContext.ts';

import getPeriodNumber from 'pages/Admin/LegacyCalendar/util/getPeriodNumber.ts';

import { getApiErrorMessage } from 'utils/getApiErrorMessage.ts';

import StudentDriveOverview from './StudentDriveOverview.tsx';

function formatDln(number: string) {
  if (number === null) {
    return '';
  }
  const dlnNoDashes = number.replace('-', '').replace('-', '');
  return `${dlnNoDashes.substring(0, 4)}-${dlnNoDashes.substring(4, 2)}-${dlnNoDashes.substring(6, 4)}`;
}

interface PropTypes {
  // student: CoursePeriod['period_enrollment'][number]['student'];
  periodEnrollment: CoursePeriod['period_enrollments'][number];
}

export default function StudentOverviewV2(props: PropTypes) {
  const { periodEnrollment } = props;
  const queryClient = useQueryClient();
  // const { openPermitUpdateDialog, PermitUpdateDialog } = usePermitUpdateDialog([
  //   'course_period',
  //   periodEnrollment.course_period_id,
  // ]);
  const dialogs = useDialogs();
  const [attendanceAnchorEl, setAttendanceAnchorEl] = useState(null);
  const openAttendanceMenu = Boolean(attendanceAnchorEl);
  const [modifyRegistrationAnchorEl, setModifyRegistrationAnchorEl] = useState(null);
  const openModifyRegistrationMenu = Boolean(modifyRegistrationAnchorEl);
  const auth = useAuthContext();

  const handleClickAttendanceMenu = (event: any) => {
    setAttendanceAnchorEl(event.currentTarget);
  };
  const handleCloseAttendanceMenu = () => {
    setAttendanceAnchorEl(null);
  };

  const handleClickModifyRegistrationMenu = (event: any) => {
    setModifyRegistrationAnchorEl(event.currentTarget);
  };
  const handleCloseModifyRegistrationMenu = () => {
    setModifyRegistrationAnchorEl(null);
  };

  const getAttendanceName = (attendance: 'present' | 'absent' | 'no_permit' | null) => {
    switch (attendance) {
      case 'present':
        return 'Present';
      case 'absent':
        return 'No Show';
      case 'no_permit':
        return 'No Permit';
      default:
        return 'Take Attendance';
    }
  };

  // useEffect(() => {
  //   const subscription = (API.graphql(graphqlOperation(onUpdateStudent, { username: student.username })) as any).subscribe({
  //     next: () => {
  //       // const updatedStudent = value.data.onUpdateStudent;
  //       queryClient.invalidateQueries(['driveOverviewDialogV2', currentRegistration.driveID]);
  //     },
  //     // eslint-disable-next-line no-console
  //     error: (error: any) => {
  //       captureException(error);
  //     },
  //   });
  //   return (() => {
  //     subscription.unsubscribe();
  //   });
  // }, []);

  const attendanceMutation = useTakeAttendance({
    onMutate: async (params: TakeAttendanceParams) => {
      handleCloseAttendanceMenu();
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: ['course_period', { id: periodEnrollment.course_period_id }],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([
        'course_period',
        { id: periodEnrollment.course_period_id },
      ]) as CoursePeriod;
      const newData = queryClient.getQueryData([
        'course_period',
        { id: periodEnrollment.course_period_id },
      ]) as CoursePeriod;

      params.attendanceRecords.forEach((newValues) => {
        // const newData = queryClient.getQueryData(['course_period', periodEnrollment.course_period_id]) as CoursePeriod;
        const periodEnrollmentIndex = previousData.period_enrollments.findIndex(
          (enrollment) =>
            enrollment.course_period_id === periodEnrollment.course_period_id &&
            enrollment.student_user_id === periodEnrollment.student_user_id,
        );
        newData.period_enrollments[periodEnrollmentIndex].attendance = newValues.attendance;
      });

      // Optimistically update to the new value
      queryClient.setQueryData(['course_period', { id: periodEnrollment.course_period_id }], newData);

      // Return a context object with the snapshotted value
      return { previousData };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course_period', { id: periodEnrollment.course_period_id }] });
      queryClient.invalidateQueries({ queryKey: ['course_periods'] });
      queryClient.invalidateQueries({ queryKey: ['student_course_periods'] });
    },
    onError(error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error ',
        message: getApiErrorMessage(error),
      });
    },
  });

  const cancelDrive = useCancelPeriodEnrollment({
    onMutate: async () => {
      handleCloseModifyRegistrationMenu();
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['course_period', { id: periodEnrollment.course_period_id }] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData([
        'course_period',
        { id: periodEnrollment.course_period_id },
      ]) as CoursePeriod;
      const newData = queryClient.getQueryData([
        'course_period',
        { id: periodEnrollment.course_period_id },
      ]) as CoursePeriod;

      const periodEnrollmentIndex = previousData.period_enrollments.findIndex(
        (enrollment) =>
          enrollment.course_period_id === periodEnrollment.course_period_id &&
          enrollment.student_user_id === periodEnrollment.student_user_id,
      );
      newData.period_enrollments[periodEnrollmentIndex].canceled_at = new Date().toISOString();

      // Optimistically update to the new value
      queryClient.setQueryData(['course_period', { id: periodEnrollment.course_period_id }], newData);

      // Return a context object with the snapshotted value
      return { previousData };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: () => {
      handleCloseModifyRegistrationMenu();
      queryClient.invalidateQueries({ queryKey: ['course_period', { id: periodEnrollment.course_period_id }] });
      queryClient.invalidateQueries({ queryKey: ['course_periods'] });
      queryClient.invalidateQueries({ queryKey: ['student_course_periods'] });
    },
    onError(error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error ',
        message: getApiErrorMessage(error),
      });
    },
  });

  const deleteRegistration = useDeletePeriodEnrollment({
    onSuccess: () => {
      handleCloseModifyRegistrationMenu();
      queryClient.invalidateQueries({ queryKey: ['course_period', { id: periodEnrollment.course_period_id }] });
      queryClient.invalidateQueries({ queryKey: ['course_periods'] });
      queryClient.invalidateQueries({ queryKey: ['student_course_periods'] });
    },
  });

  // const deleteRegistration = useMutation(async () => await API.graphql({
  //   query: deleteStudentDrive,
  //   variables: { input: { id: 'currentRegistration.id' } },
  // }) as any, {
  //   // eslint-disable-next-line @typescript-eslint/no-unused-vars
  //   onSuccess: (data, variables) => {
  //     handleCloseAttendanceMenu();
  //     // const newData = queryClient.getQueryData(['drive', currentRegistration.driveID]) as any;
  //     // const registrationIndex = newData.items.findIndex((registration: StudentDrive) => registration.id === currentRegistration.id);
  //     // const studentDriveIndex = newData.items[registrationIndex].student.drives.items.findIndex((registration: StudentDrive) => registration.id === currentRegistration.id);
  //     // newData.items[registrationIndex].canceled = data.data.updateStudentDrive.canceled;
  //     // newData.items[registrationIndex].canceledOn = data.data.updateStudentDrive.canceledOn;
  //     // newData.items[registrationIndex].student.drives.items[studentDriveIndex].canceled = data.data.updateStudentDrive.canceled;
  //     // newData.items[registrationIndex].student.drives.items[studentDriveIndex].canceledOn = data.data.updateStudentDrive.canceledOn;
  //
  //     // queryClient.setQueryData(['drive', currentRegistration.driveID], newData);
  //     // queryClient.invalidateQueries(['drive', 'currentRegistration.driveID']);
  //     // queryClient.invalidateQueries(['driveOverviewDialogV2', 'currentRegistration.driveID']);
  //   },
  // });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderItem = (
    label: string,
    value: string | null | undefined,
    capitalize: boolean | null = true,
    highLightColor = 'none',
    textColor = 'default',
  ) => {
    if (value === null || value === undefined) {
      return null;
    }
    let transform: any;
    switch (capitalize) {
      case false:
        transform = 'lowercase';
        break;
      case true:
        transform = 'capitalize';
        break;
      default:
        transform = 'unset';
    }
    return (
      <Box key={uuidv4()} display="flex" py={0} mr={0} sx={{ bgcolor: highLightColor, color: textColor }}>
        <Typography variant="button" fontWeight="bold" textTransform="capitalize">
          {label}: &nbsp;
        </Typography>
        <Typography variant="button" fontWeight="regular" color="text" textTransform={transform}>
          &nbsp;
          {value}
        </Typography>
      </Box>
    );
  };

  // const renderCustom = (
  //   label: string,
  //   value: ReactNode,
  //   capitalize: boolean | null = true,
  //   highLightColor = 'none',
  //   textColor = 'default',
  // ) => {
  //   if (value === null || value === undefined) {
  //     return null;
  //   }
  //   let transform: any;
  //   switch (capitalize) {
  //     case false:
  //       transform = 'lowercase';
  //       break;
  //     case true:
  //       transform = 'capitalize';
  //       break;
  //     default:
  //       transform = 'unset';
  //   }
  //   return (
  //     <Box key={uuidv4()} display="flex" py={0} mr={0} sx={{ bgcolor: highLightColor, color: textColor }}>
  //       <Typography variant="button" fontWeight="bold" textTransform={transform}>
  //         {label}: &nbsp;
  //       </Typography>
  //       {value}
  //     </Box>
  //   );
  // };

  const renderEditable = (
    label: string,
    value: string | null | undefined,
    onClickEdit: () => void,
    capitalize: boolean | null = true,
    highLightColor = 'none',
    textColor = 'default',
  ) => {
    if (value === null || value === undefined) {
      return null;
    }
    let transform: any;
    switch (capitalize) {
      case false:
        transform = 'lowercase';
        break;
      case true:
        transform = 'capitalize';
        break;
      default:
        transform = 'unset';
    }
    return (
      <Box key={uuidv4()} display="flex" py={0} mr={0} sx={{ bgcolor: highLightColor, color: textColor }}>
        <Typography variant="button" fontWeight="bold" textTransform={transform}>
          {label}: &nbsp;
        </Typography>

        {/* <Button variant="text" onClick={onClickEdit} sx={{ p: 0 }}> */}
        {/*  <Typography */}
        {/*    variant="button" */}
        {/*    // color="text" */}
        {/*    textTransform="capitalize" */}
        {/*    sx={{ */}
        {/*      fontWeight: 'bold', */}
        {/*      // textDecoration: 'underline', */}
        {/*      textDecorationThickness: '3px', */}
        {/*    }} */}
        {/*  > */}
        {/*    {value} */}
        {/*  </Typography> */}
        {/* </Button> */}

        <Typography variant="button" fontWeight="regular" color="text" textTransform={transform}>
          &nbsp;
          {value}
        </Typography>
        <IconButton aria-label="edit" size="small" sx={{ py: 0 }} onClick={onClickEdit}>
          <EditIcon color="primary" />
        </IconButton>
      </Box>
    );
  };

  const getPermitExpiration = (date: any) => {
    if (date === null) {
      return renderItem('DLN Expiration', 'No Permit On File');
    }
    const expDate = moment(date);
    let highlightColor = 'none';
    let textColor = 'default';
    const daysUntilExp = Math.ceil(expDate.diff(moment(), 'days', true));
    let daysLeft = '';

    if (daysUntilExp < 0) {
      // expired
      highlightColor = 'red';
      textColor = '#ffffff';
      // daysLeft = ` (Expired ${Math.abs(daysUntilExp)} ${Math.abs(daysUntilExp) === 1 ? 'Day' : 'Days'} Ago)`;
      daysLeft = ` (Expired ${expDate.fromNow()})`;
    } else if (daysUntilExp === 0) {
      // expired today
      highlightColor = 'red';
      textColor = '#ffffff';
      daysLeft = ' (Expired Today)';
    } else if (daysUntilExp > 0 && daysUntilExp <= 30) {
      // expires within 30 days
      highlightColor = 'orange';
      textColor = '#000000';
      // daysLeft = Math.abs(daysUntilExp) === 1 ? ' (Expires Tomorrow)' : ` (Expires In ${daysUntilExp} Days)`;
      daysLeft = Math.abs(daysUntilExp) === 1 ? ' (Expires Tomorrow)' : ` (Expires ${expDate.fromNow()})`;
    }
    // return renderEditable('DLN Expiration', (permit !== undefined && permit !== null ? expDate.format('MM/DD/YYYY') + daysLeft : null) as string, () => (openPermitUpdateDialog(student)), true, highlightColor, textColor);
    return renderEditable(
      'DLN Expiration',
      (periodEnrollment.student.permit_num ? expDate.format('MM/DD/YYYY') + daysLeft : null) as string,
      // () => openPermitUpdateDialog(periodEnrollment.student.user_id),
      () => {},
      true,
      highlightColor,
      textColor,
    );
  };

  return (
    <Grid container columns={2} flexWrap="nowrap">
      <Grid item xs={1}>
        <Typography variant="h6" fontWeight="medium" textTransform="capitalize">
          Student
        </Typography>
        <Grid item px="8px" py={0}>
          {auth?.userRole === 'ADMIN' && (
            <Link
              to={`/admin/students/${periodEnrollment.student_user_id}`}
              style={{
                textDecoration: 'none',
                color: 'unset',
              }}
            >
              {renderItem(
                'Name',
                `${periodEnrollment.student.user.first_name} ${periodEnrollment.student.user.middle_name} ${periodEnrollment.student.user.last_name}`,
              )}
            </Link>
          )}
          {auth?.userRole !== 'ADMIN' &&
            renderItem(
              'Name',
              `${periodEnrollment.student.user.first_name} ${periodEnrollment.student.user.middle_name} ${periodEnrollment.student.user.last_name}`,
            )}
          {renderEditable(
            'DLN #',
            periodEnrollment.student.permit_num
              ? formatDln(periodEnrollment.student.permit_num as string)
              : 'No Permit On File',
            () => {
              // openPermitUpdateDialog(periodEnrollment.student.user_id);
            },
            true,
            '#ffffff',
            periodEnrollment.student.permit_num ? '#ff0000' : undefined,
          )}
          {periodEnrollment.student.permit_expiration &&
            getPermitExpiration(periodEnrollment.student.permit_expiration)}
          {periodEnrollment.student.user.user_phones.map((phone) => renderItem(phone.type ?? '', phone.phone))}
          {/* {renderItem('Mobile', student!.primaryPhone!)} */}
          {/* {renderItem('Home', student!.secondaryPhone!)} */}
          {/* {renderItem('Need To Complete By', moment(student.signUpDate).add(180, 'days').format('MM/DD/YYYY'), false, 'yellow')} */}
          {/* {renderItem('Drive ID', currentRegistration.driveID, false)} */}
          {/* {renderItem('Registration ID', currentRegistration.id, null)} */}
          {/* {renderItem('Student ID', currentRegistration.studentID, false)} */}
          <Box display="flex" py={0} mr={2} mb={-0.5} flexWrap="nowrap">
            <Typography variant="button" fontWeight="bold" textTransform="capitalize">
              Attendance: &nbsp;
            </Typography>
            {!periodEnrollment.canceled_at && (
              <Button
                id="basic-button"
                variant="text"
                aria-controls={openAttendanceMenu ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openAttendanceMenu ? 'true' : undefined}
                onClick={handleClickAttendanceMenu}
                sx={{ p: 0 }}
              >
                <Typography
                  variant="button"
                  // fontWeight="regular"
                  color="text"
                  textTransform="capitalize"
                  noWrap
                  sx={
                    !periodEnrollment.attendance
                      ? {
                          fontWeight: 'bold',
                          color: '#ff3a30',
                          textDecoration: 'underline',
                          textDecorationThickness: '3px',
                          // whiteSpace: 'nowrap',
                          // height: '100%'
                        }
                      : null
                  }
                >
                  {/* &nbsp; */}
                  {/*{periodEnrollment.attendance ? _.startCase(periodEnrollment.attendance) : 'Take Attendance'}*/}
                  {getAttendanceName(periodEnrollment.attendance as 'present' | 'absent' | 'no_permit' | null)}
                </Typography>
              </Button>
            )}

            {periodEnrollment.canceled_at && (
              <Typography variant="button" fontWeight="regular" color="text">
                &nbsp; N/A
              </Typography>
            )}

            {/*{periodEnrollment.attendance_taken_by_user && (*/}
            {/*    <Typography variant="caption" textTransform="capitalize">*/}
            {/*      Taken by Some User at some time*/}
            {/*    </Typography>*/}
            {/*)}*/}
          </Box>
          {periodEnrollment.attendance_taken_by_user && (
            <Typography variant="caption" textTransform="capitalize" sx={{ lineHeight: -1 }}>
              {/*{`Taken By ${periodEnrollment.attendance_taken_by_user.first_name} ${periodEnrollment.attendance_taken_by_user.last_name}`}*/}
              {`${moment(periodEnrollment.attendance_taken_at).calendar(null, {
                sameElse: 'MMM D, YYYY [at] h:mm a',
              })} by ${periodEnrollment.attendance_taken_by_user.nickname ?? periodEnrollment.attendance_taken_by_user.first_name ?? ''} ${periodEnrollment.attendance_taken_by_user.last_name ?? ''}
                `}
            </Typography>
          )}
          {/* {renderItem('Drive ID', currentRegistration.driveID, false)} */}
          {/* {renderItem('Registration ID', currentRegistration.id, null)} */}
          {/* {renderItem('Student ID', currentRegistration.studentID, false)} */}
        </Grid>
        <Menu
          id="basic-menu"
          anchorEl={attendanceAnchorEl}
          open={openAttendanceMenu}
          onClose={handleCloseAttendanceMenu}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          sx={{ zIndex: 100000 }}
          TransitionComponent={Fade}
        >
          <MenuItem
            selected={periodEnrollment.attendance === 'present'}
            onClick={() =>
              attendanceMutation.mutate({
                coursePeriodId: periodEnrollment.course_period_id,
                attendanceRecords: [
                  {
                    studentId: periodEnrollment.student_user_id,
                    attendance: 'present',
                  },
                ],
              })
            }
          >
            Present
          </MenuItem>
          <MenuItem
            selected={periodEnrollment.attendance === 'absent'}
            onClick={() =>
              attendanceMutation.mutate({
                coursePeriodId: periodEnrollment.course_period_id,
                attendanceRecords: [
                  {
                    studentId: periodEnrollment.student_user_id,
                    attendance: 'absent',
                  },
                ],
              })
            }
          >
            No-Show
          </MenuItem>
          <MenuItem
            selected={periodEnrollment.attendance === 'no_permit'}
            onClick={() =>
              attendanceMutation.mutate({
                coursePeriodId: periodEnrollment.course_period_id,
                attendanceRecords: [
                  {
                    studentId: periodEnrollment.student_user_id,
                    attendance: 'no_permit',
                  },
                ],
              })
            }
          >
            No Permit
          </MenuItem>
          <MenuItem
            selected={!periodEnrollment.attendance}
            onClick={() =>
              attendanceMutation.mutate({
                coursePeriodId: periodEnrollment.course_period_id,
                attendanceRecords: [
                  {
                    studentId: periodEnrollment.student_user_id,
                    attendance: null,
                  },
                ],
              })
            }
          >
            None
          </MenuItem>
        </Menu>

        <Box display="flex" py={0} mr={2} pt="16px">
          {auth?.userRole === 'ADMIN' && (
            <Button
              variant="contained"
              // aria-controls={openModifyRegistrationMenu ? 'basic-menu' : undefined}
              // aria-haspopup="true"
              // aria-expanded={openModifyRegistrationMenu ? 'true' : undefined}
              onClick={handleClickModifyRegistrationMenu}
              endIcon={<KeyboardArrowDownIcon />}
            >
              <Typography variant="button" textTransform="capitalize">
                Modify Registration
              </Typography>
            </Button>
          )}
          <Menu
            id="basic-menu"
            anchorEl={modifyRegistrationAnchorEl}
            open={openModifyRegistrationMenu}
            onClose={handleCloseModifyRegistrationMenu}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            sx={{ zIndex: 100000 }}
            TransitionComponent={Fade}
          >
            {!periodEnrollment.canceled_at && (
              <MenuItem
                onClick={() =>
                  cancelDrive.mutate({
                    coursePeriodId: periodEnrollment.course_period_id,
                    studentId: periodEnrollment.student_user_id,
                    cancel: true,
                    overrideLateFee: false,
                  })
                }
              >
                Cancel Registration
              </MenuItem>
            )}
            {!periodEnrollment.canceled_at && (
              <MenuItem
                onClick={() =>
                  cancelDrive.mutate({
                    coursePeriodId: periodEnrollment.course_period_id,
                    studentId: periodEnrollment.student_user_id,
                    cancel: true,
                    overrideLateFee: true,
                  })
                }
              >
                Cancel Registration And Mark Not Late
              </MenuItem>
            )}
            {periodEnrollment.canceled_at && (
              <MenuItem
                onClick={() =>
                  cancelDrive.mutate({
                    coursePeriodId: periodEnrollment.course_period_id,
                    studentId: periodEnrollment.student_user_id,
                    cancel: false,
                  })
                }
              >
                Undo Cancellation
              </MenuItem>
            )}
            <MenuItem
              onClick={() =>
                deleteRegistration.mutate({
                  coursePeriodId: periodEnrollment.course_period_id,
                  studentId: periodEnrollment.student_user_id,
                })
              }
            >
              Delete Registration
            </MenuItem>
          </Menu>
        </Box>

        {periodEnrollment.student.parents &&
          periodEnrollment.student.parents.map((parent) => (
            <Box key={uuidv4()}>
              <Box display="flex" justifyContent="space-between" alignItems="center" pt={2} px={1}>
                <Typography variant="h6" fontWeight="medium" textTransform="capitalize">
                  {parent.relation ? parent.relation : 'Parent'}
                </Typography>
              </Box>
              <Box px={2} py={1}>
                <Box>
                  {renderItem('Name', `${parent.first_name} ${parent.last_name}`)}
                  {/* {renderItem('Email', parent.email, false)} */}
                  {parent.cell_phone && renderItem('Cell', parent.cell_phone, false)}
                  {parent.home_phone && renderItem('Home', parent.home_phone, false)}
                  {parent.home_phone &&
                    renderItem(
                      'Work',
                      parent.work_extension
                        ? `${parent.work_phone}, ext. ${parent.work_extension}.`
                        : parent.work_phone,
                      false,
                    )}
                  {/* {parent.phoneNumbers.map((phone: any) => renderItem(phone.type, phone.ext ? `${phone.phone}, ext. ${phone.ext}.` : phone.phone, false))} */}
                </Box>
              </Box>
            </Box>
          ))}
      </Grid>
      <Grid item overflow="auto">
        <Typography
          variant="h6"
          fontWeight="medium"
          textTransform="capitalize"
          sx={{
            position: 'sticky',
            top: '0px',
            zIndex: 1000,
            backgroundColor: '#ffffff',
          }}
        >
          Other Course Periods
        </Typography>
        <List sx={{ maxHeight: '50vh' }}>
          {periodEnrollment.student.period_enrollments
            ?.slice() // create a shallow copy to avoid mutating original array
            .sort((a, b) => {
              const dateA = new Date(a.course_period?.start || 0).getTime();
              const dateB = new Date(b.course_period?.start || 0).getTime();
              return dateA - dateB;
            })
            .map((enrollment) => {
              console.log(enrollment);
              return (
                <ListItem
                  key={uuidv4()}
                  sx={{
                    // p: 0,
                    my: 1,
                  }}
                >
                  <StudentDriveOverview
                    driveNumber={getPeriodNumber(
                      enrollment.course_period_id,
                      periodEnrollment.student.period_enrollments,
                    )}
                    registration={enrollment}
                    isCurrent={enrollment.course_period_id === periodEnrollment.course_period_id}
                  />
                </ListItem>
              );
            })}
        </List>
      </Grid>
    </Grid>
  );
}
